import { useParams, useRouteContext } from "@tanstack/react-router";
import type React from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import type { communityShell_club$key } from "./__generated__/communityShell_club.graphql";
import type { communityShell_user$key } from "./__generated__/communityShell_user.graphql";
import type { communityShellQuery } from "./__generated__/communityShellQuery.graphql";
import { CommunitySidebar } from "./community-sidebar";
import { Portals } from "./portals";
import { SidebarPreview } from "./sidebar-preview";

export function CommunityShell({ children }: { children: React.ReactNode }) {
	const { club } = useRouteContext({ from: "__root__" });
	const params = useParams({
		from: "/_visibility/_topnav/comunidade/s/$space",
		shouldThrow: false,
	});

	const data = useLazyLoadQuery<communityShellQuery>(
		graphql`
    query communityShellQuery($slug: String!, $space: String!, $skip: Boolean!) {
        club(slug: $slug) {
          ...communityShell_club

          space(slug: $space) @skip(if: $skip) {
            isMember
            visibility
          }
        }
        user {
          ...communityShell_user @arguments(slug: $slug)
        }
    }
  `,
		{ slug: club.slug, space: params?.space || "", skip: !params?.space },
	);
	const clubData = useFragment<communityShell_club$key>(
		graphql`
    fragment communityShell_club on Club {
      id
      ...communitySidebarFragment
      ...sidebarPreview
      ...portals_club
    }
  `,
		data.club,
	);

	const userData = useFragment<communityShell_user$key>(
		graphql`
	    fragment communityShell_user on User
			@argumentDefinitions(
			    slug: { type: "String!"}
			)
			{
			  ...portals_user @arguments(club: $slug)
			}
	`,
		data.user,
	);

	const mustShowPaywall =
		!club?.permissions?.length &&
		data.club.space &&
		!data.club.space.isMember &&
		data.club.space.visibility !== "PUBLIC";

	return (
		<div className="flex flex-row min-h-full">
			<CommunitySidebar club={clubData} />
			<div className="flex justify-start pb-6 flex-col items-center mt-6 w-full flex-1 px-6">
				{mustShowPaywall ? null : (
					<div className="w-full xl:max-w-[1000px] empty:hidden not-empty:mb-6">
						<Portals club={clubData} user={userData} />
					</div>
				)}
				<div className="xl:max-w-[1000px] gap-6 w-full flex flex-row justify-between items-start">
					{children}

					{mustShowPaywall ? null : <SidebarPreview club={clubData} />}
				</div>
			</div>
		</div>
	);
}
